import React, { useEffect, useState } from "react";
import styles from "./CatalogAssets.module.css";

// [[Catalog Assets]] #obsidian-marker
export default function CatalogAssets({ partNumber }) {
    const [state, setState] = useState({ partNumber, loading: true, materials: [] });
    const [attempt, setAttempt] = useState(0);
    useEffect(() => {
        if (!partNumber) return undefined;
        const controller = new AbortController();
        setState({ partNumber, loading: true, materials: [] });
        fetch(`/api/components/assets?${new URLSearchParams({ partNumber })}`, { signal: controller.signal })
            .then(response => {
                if (!response.ok) throw new Error("Не удалось загрузить материалы");
                return response.json();
            })
            .then(materials => {
                if (!controller.signal.aborted) setState({ partNumber, loading: false, materials });
            })
            .catch(error => {
                if (!controller.signal.aborted) setState({ partNumber, loading: false, materials: [], error: error.message });
            });
        return () => controller.abort();
    }, [partNumber, attempt]);

    if (!partNumber) return null;
    if (state.partNumber !== partNumber || state.loading) return <p role="status">Загрузка материалов…</p>;
    if (state.error) return <div className={styles.section} role="status">{state.error} <button type="button" onClick={() => setAttempt(value => value + 1)}>Повторить</button></div>;
    const materials = state.materials.map(item => ({ ...item, assets: (item.assets || []).filter(asset => asset.stored) })).filter(item => item.assets?.length || Object.keys(item.parameters || {}).length);
    if (!materials.length) return null;
    return <section className={styles.section} aria-label="Материалы компонента">
        <h3>Изображения и спецификации</h3>
        {materials.map(material => <div key={material.component_id}>
            <h4>{material.manufacturer}</h4>
            <div className={styles.files}>{material.assets.map(asset => <AssetFile key={`${material.component_id}-${asset.kind}`} asset={asset} partNumber={partNumber} />)}</div>
            {Object.keys(material.parameters || {}).length > 0 && <details>
                <summary>Характеристики DigiKey</summary>
                <dl className={styles.parameters}>{Object.entries(material.parameters).map(([key, value]) => {
                    const label = value && typeof value === "object" ? value.ParameterText || value.parameter_text || key : key;
                    const display = value && typeof value === "object" ? value.ValueText || value.value_text || JSON.stringify(value) : String(value ?? "—");
                    return <React.Fragment key={key}><dt>{label}</dt><dd>{display}</dd></React.Fragment>;
                })}</dl>
            </details>}
        </div>)}
    </section>;
}

export function AssetFile({ asset, partNumber }) {
    const [state, setState] = useState({ loading: false, url: null, error: null });
    const [attempt, setAttempt] = useState(0);
    useEffect(() => {
        if (!asset.stored) return undefined;
        const controller = new AbortController();
        let objectUrl;
        setState({ loading: true, url: null, error: null });
        fetch(asset.url, { signal: controller.signal })
            .then(response => {
                if (!response.ok) throw new Error("Файл временно недоступен");
                return response.blob();
            })
            .then(blob => {
                if (controller.signal.aborted) return;
                objectUrl = URL.createObjectURL(blob);
                setState({ loading: false, url: objectUrl, error: null });
            })
            .catch(error => {
                if (!controller.signal.aborted) setState({ loading: false, url: null, error: error.message });
            });
        return () => {
            controller.abort();
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [asset.url, asset.stored, attempt]);
    const label = asset.kind === "image" ? "Изображение" : "Спецификация PDF";
    if (!asset.stored) return null;
    const url = state.url;
    if (state.error) return <div className={styles.feedback} role="status">{state.error} <button className={styles.link} type="button" onClick={() => { setState({ loading: false, url: null, error: null }); setAttempt(value => value + 1); }}>Повторить</button></div>;
    if (!url || (asset.stored && state.loading)) return <div className={styles.feedback} role="status">Загрузка: {label}…</div>;
    return <div className={styles.file}>
        {asset.kind === "image" ? <a className={styles.imageStage} href={url} target="_blank" rel="noopener noreferrer" aria-label={`Открыть оригинал изображения ${partNumber}`}>
            <img key={`${url}-${attempt}`} className={styles.image} src={url} alt={partNumber} onError={() => setState(current => ({ ...current, error: "Не удалось показать изображение" }))} />
        </a> : <iframe className={styles.pdf} title={`Спецификация ${partNumber}`} src={url} />}
        <div className={styles.toolbar}>
            <span className={styles.hint}>{asset.kind === "image" ? "Нажмите на изображение, чтобы открыть оригинал" : "Если PDF не отображается, откройте его в новой вкладке"}</span>
            <a className={styles.link} href={url} target="_blank" rel="noopener noreferrer">Открыть в новой вкладке ↗</a>
        </div>
    </div>;
}
